import { useEffect, useState } from 'react';
import axios from 'axios';
import { Network, Trash2, Plus, Pencil, Check, X, Lock } from 'lucide-react';

interface AthenaTenantMapping {
  id: number;
  tenant_id: number;
  athena_tenant_id: string;
  label: string | null;
  allowed_ips: string | null;
  require_tls: boolean;
  created_at: string;
}

// Mapping athena_tenant_id -> tenant per l'ingestion PUSH di Sangfor Athena XDR (vedi
// backend/src/ingestion-gateway.ts + db/database.ts athena_tenant_mappings): a differenza
// dell'integrazione EDR sopra (che interroga attivamente l'API Athena), qui Athena invia i log
// via syslog/TCP sullo stesso canale mTLS dei broker, e un'unica connessione può contenere log di
// più clienti finali (sottogruppi Athena) mescolati. Ogni sottogruppo va mappato esplicitamente al
// tenant corretto: senza un mapping configurato, i relativi log vengono scartati invece di essere
// assegnati al tenant sbagliato.
export default function AthenaTenantMappingPanel({ tenantId }: { tenantId: number | null }) {
  const [mappings, setMappings] = useState<AthenaTenantMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [athenaTenantId, setAthenaTenantId] = useState('');
  const [label, setLabel] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [requireTls, setRequireTls] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editIps, setEditIps] = useState('');
  const [editTls, setEditTls] = useState(false);

  function load() {
    if (!tenantId) return;
    setLoading(true);
    axios.get('/api/athena-tenant-mapping', { params: { tenantId } })
      .then(res => setMappings(res.data.data))
      .finally(() => setLoading(false));
  }

  useEffect(load, [tenantId]);

  if (!tenantId) return null;

  async function add() {
    if (!athenaTenantId.trim()) return;
    setSaving(true);
    setError('');
    try {
      await axios.post('/api/athena-tenant-mapping', {
        tenantId, athenaTenantId: athenaTenantId.trim(), label: label.trim() || undefined,
        allowedIps: allowedIps.trim() || undefined,
        requireTls,
      });
      setAthenaTenantId('');
      setLabel('');
      setAllowedIps('');
      setRequireTls(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add mapping');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number) {
    await axios.delete(`/api/athena-tenant-mapping/${id}`);
    load();
  }

  // Audit 23/10/2026 (A6): IP sorgente ammessi per il singolo mapping, modificabili in riga.
  async function saveIps(id: number) {
    setError('');
    try {
      await axios.put(`/api/athena-tenant-mapping/${id}`, { allowedIps: editIps.trim(), requireTls: editTls });
      setEditingId(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update mapping');
    }
  }

  return (
    <div className="card p-4 mb-4">
      <h2 className="font-medium text-sm text-gray-900 mb-1 inline-flex items-center gap-2">
        <Network size={15} /> Sangfor Athena XDR tenant mapping
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        Maps an Athena XDR tenant id (sent as "tenant"/"customer" in each pushed log, e.g. 10001001) to this
        tenant. Required for logs pushed by Athena to the dedicated push port (6515, TCP/JSON, source IPs
        allowed in Platform Settings): a tenant id with no mapping is dropped instead of being ingested under
        the wrong tenant. Optionally restrict each mapping to its own source IPs: when set, a pushed log for
        that Athena tenant id is accepted only from those addresses (on top of the global allowlist). "Require TLS"
        accepts the mapping's logs only from the encrypted push port (6516), so each customer can move to TLS on
        its own before the plain port is closed for everyone. See Guides → "Collegare Sangfor Athena XDR in push".
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          {mappings.length > 0 && (
            <div className="space-y-1 mb-3">
              {mappings.map(m => (
                <div key={m.id} className="text-sm border border-gray-100 rounded px-2 py-1">
                  <div className="flex items-center justify-between">
                    <span>
                      <span className="font-mono text-xs text-gray-700">{m.athena_tenant_id}</span>
                      {m.label && <span className="text-gray-400 ml-2">{m.label}</span>}
                      {m.require_tls && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5"><Lock size={10} /> TLS only</span>}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <button onClick={() => { setEditingId(m.id); setEditIps(m.allowed_ips || ''); setEditTls(m.require_tls); }}
                        className="text-gray-400 hover:text-primary-600" title="Edit allowed source IPs">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(m.id)} className="text-gray-400 hover:text-red-500" title="Remove mapping">
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </div>
                  {editingId === m.id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input value={editIps} onChange={e => setEditIps(e.target.value)} autoFocus
                        className="input-field font-mono text-xs flex-1" placeholder="203.0.113.9, 198.51.100.0/24 (empty = global allowlist only)" />
                      <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                        <input type="checkbox" checked={editTls} onChange={e => setEditTls(e.target.checked)} /> Require TLS
                      </label>
                      <button onClick={() => saveIps(m.id)} className="text-green-600 hover:text-green-700" title="Save"><Check size={14} /></button>
                      <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600" title="Cancel"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      Source IPs: {m.allowed_ips ? <span className="font-mono text-gray-600">{m.allowed_ips}</span> : 'global allowlist only'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Athena subgroup ID</label>
              <input value={athenaTenantId} onChange={e => setAthenaTenantId(e.target.value)}
                className="input-field font-mono text-xs" placeholder="e.g. 10001001" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Label (optional)</label>
              <input value={label} onChange={e => setLabel(e.target.value)}
                className="input-field text-xs" placeholder="e.g. customer's Athena subgroup" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Allowed source IPs (optional)</label>
              <input value={allowedIps} onChange={e => setAllowedIps(e.target.value)}
                className="input-field font-mono text-xs" placeholder="203.0.113.9, 198.51.100.0/24" />
            </div>
            <label className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap pb-2">
              <input type="checkbox" checked={requireTls} onChange={e => setRequireTls(e.target.checked)} /> Require TLS
            </label>
            <button onClick={add} disabled={saving || !athenaTenantId.trim()}
              className="btn-primary text-sm inline-flex items-center gap-1">
              <Plus size={14} /> Add
            </button>
          </div>
          {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        </>
      )}
    </div>
  );
}
